import CmpJSApi from './cmp-js-api';
import Constants from '../constants';
import { DataService } from './utilities/data.service';
import { UtilityService } from './utility.service';
const Cookies = require('js-cookie');
const { ConsentString } = require('consent-string');
const packageJson = require('../../../package.json');

/**
 * This class is used to create consents string to be saved in the cookie.
 * Create consentsString.
 */
export default class CmpIABService {
    private _iabIsActive: boolean;
    private CONSENSU_IFRAME_PAGE_NAME: string = 'consensuIframeIndex.html';
    public purposes: IAB.Purpose[] = [];
    public purposeStatus: IAB.PurposeStatus = {};
    public features: IAB.Feature[] = [];
    public vendors: IAB.Vendor[] = [];
    private globalVendorList: IAB.VendorList = null;
    public NON_IAB_IDS: string[] = ['H1Vl5NidjWX'];
    public NON_IAB_PURPOSE_STORAGE_KEY: string = 'nonIABPurpose';
    public DEFAULT_NON_IAB_PURPOSE = {
        id: 9999,
        name: 'Non IAB Vendors',
        description: 'Vendors not yet registered with IAB Framework.'
    };

    constructor(private utilityService: UtilityService) {
        this.initializeVendorListListener();
    }

    public areAllPurposesSameStatus(vendor: IAB.Vendor, status: boolean) {
        const purposesStatusCount = this.purposes
            .filter(purpose => vendor.purposeIds.indexOf(purpose.id) > -1 && this.purposeStatus[purpose.id] === status)
            .length;
        if (purposesStatusCount === vendor.purposeIds.length) {
            return true;
        }
        return false;
    }

    private initializeVendorListListener(): void {
        window.addEventListener('message', (payload) => {
            if (payload.data.vendorList) {
                this.globalVendorList = JSON.parse(JSON.stringify(payload.data.vendorList));
                if (payload.data.vendorList.vendors) {
                    this.vendors = this.filterVendors(payload.data.vendorList.vendors);
                } else {
                    this.globalVendorList.vendors = this.vendors;
                }
                
                this.purposes = this.mapVendorListToPurposes(payload.data.vendorList.purposes, this.vendors);
                this.features = payload.data.vendorList.features;
                this.utilityService.UserCentricViewController.updateView();
            }
        });
    }

    public getFeatureStatus(): IAB.PurposeStatus {
        return DataService.getLocalStorage('features') || {};
    }

    public getIabPurposeStatus(): IAB.PurposeStatus {
        const enabledPurposes: number[] = this.getEnabledPurposesFromCookie();
        const purposeStatus: IAB.PurposeStatus = {};
        enabledPurposes.forEach((id: number) => {
            purposeStatus[id] = true;
        });
        purposeStatus[this.DEFAULT_NON_IAB_PURPOSE.id] = this.initializeNonIabPurposeStatus();
        return purposeStatus;
    }

    private initializeNonIabPurposeStatus(): boolean {
        const localNonIabPurpose: boolean = DataService.getLocalStorage(this.NON_IAB_PURPOSE_STORAGE_KEY);
        if (!localNonIabPurpose || typeof localNonIabPurpose !== 'boolean') {
            DataService.storeToLocalStorage(this.NON_IAB_PURPOSE_STORAGE_KEY, this.purposeStatus[this.DEFAULT_NON_IAB_PURPOSE.id]);
            return false;
        }
        return true;
    }
    
    public updateControllerPurposeStaus(purposeStatus: IAB.PurposeStatus): void {
        this.purposeStatus = purposeStatus;
        this.utilityService.UserCentricViewController.privacyCornerModal.secondaryAdvancePrivacyViewController.updatePurposeStatus(purposeStatus);
        this.utilityService.UserCentricViewController.privacyCenterModal.secondaryAdvancePrivacyViewController.updatePurposeStatus(purposeStatus);
        this.utilityService.UserCentricViewController.bannerModal.updatePurposeStatus(purposeStatus);
    }

    private filterVendors(vendors: IAB.Vendor[]): IAB.Vendor[] {
        const iabIds: number[] = DataService.consents
            .filter(consent =>
                DataService.consentTemplates[consent.templateId][consent.version].iabId
                && DataService.consentTemplates[consent.templateId][consent.version].iabId !== 0)
            .map(consent => Number(DataService.consentTemplates[consent.templateId][consent.version].iabId));
        return vendors.filter(vendor => iabIds.includes(vendor.id));
    }

    public mapVendorListToPurposes(purposes: IAB.Purpose[], vendors: IAB.Vendor[]): IAB.Purpose[] {
        purposes.forEach(purpose => {
            purpose.vendors = vendors.filter(vendor => vendor.purposeIds.includes(purpose.id));
        });
        purposes.push({...this.DEFAULT_NON_IAB_PURPOSE, vendors: this.getNonIabVendors(vendors)});
        return purposes;
    }

    private getNonIabVendors(vendors: IAB.Vendor[]): IAB.Vendor[] {
        const vendorIds: number[] = vendors.map((vendor: IAB.Vendor) => vendor.id);
        return DataService.consents
            .filter(consent => !consent.isHidden && !consent.isDeactivated
                && !vendorIds.includes(Number(DataService.consentTemplates[consent.templateId][consent.version].iabId)))
            .map(consent => {
                return {
                    templateId: consent.templateId,
                    name: DataService.consentTemplates[consent.templateId][consent.version].dataProcessors.join(',')
                } as IAB.Vendor;
            });
    }

    turnOnIABService() {
        this._iabIsActive = true;
        this.loadConsensuIframe();
        this.utilityService.cmpJSApi.turnOnCmpJSApi();
    }

    turnOffIabService() {
        this._iabIsActive = false;
        this.removeConsensuIframe();
        CmpJSApi.turnOffCmpJSApi();
    }

    /**
     * Loads the Iframe on the consensu.org server with a path depending on the config
     * in window.usercentrics
     */
    private loadConsensuIframe() {
        if (document.getElementById('consensuIframe')) {
            return;
        }
        const consensuIframe = document.createElement('iframe');
        consensuIframe.style.display = 'none';
        consensuIframe.style.height = '1px';
        consensuIframe.style.width = '1px';
        consensuIframe.style.position = 'fixed';
        consensuIframe.style.bottom = '0';
        consensuIframe.style.left = '0';
        consensuIframe.setAttribute('id', 'consensuIframe');

        if (window.localhost) {
            // Case 1: localhost points to local file
            consensuIframe.setAttribute('src', this.CONSENSU_IFRAME_PAGE_NAME);
        } else if (window.usercentrics.paths && window.usercentrics.paths.iabIframe) {
            // Case 2: dynamic onpremise paths are set
            consensuIframe.setAttribute('src', window.usercentrics.paths.iabIframe);
        } else if (
            // Case 3: base url is deviating (e.g. app.usercentrics.eu/clients/customer_name) but is still usercentrics
            window.usercentrics.baseUrl !== Constants.BASE_URL &&
            window.usercentrics.baseUrl.indexOf(Constants.BASE_URL) !== -1
        ) {
            const urlArray = window.usercentrics.baseUrl.split('/');
            let baseUrl = '';
            if (urlArray.length > 4) {
                baseUrl = window.usercentrics.baseUrl.slice(Constants.BASE_URL.length);
            }
            consensuIframe.setAttribute('src', `${Constants.CONSENSU_BASE_URL}${baseUrl}/${window.usercentrics.version}/${this.CONSENSU_IFRAME_PAGE_NAME}`
            );
        } else {
            // Case 4: Rest always points to latest frame
            consensuIframe.setAttribute('src', `${Constants.CONSENSU_BASE_URL}/latest/${this.CONSENSU_IFRAME_PAGE_NAME}`);
        }

        const consensuIframeId: HTMLIFrameElement = document.getElementById('consensuIframe') as HTMLIFrameElement;
        if (!consensuIframeId) {
            document.body.appendChild(consensuIframe);
        }
    }

    private removeConsensuIframe() {
        const consensuIframe = document.getElementById('consensuIframe');
        if (consensuIframe) {
            document.body.removeChild(consensuIframe);
        }
    }

    /**
     * Takes a consent string and returns the contained information about accepted vendors
     * @param consentString
     */
    static decodeConsentString(consentString: string): IAB.ConsentStringData {
        const consentStringObject = new ConsentString(consentString);

        return {
            acceptedVendors: consentStringObject.getVendorsAllowed(),
            sumOfAcceptedVendors: consentStringObject.getVendorsAllowed().length
        };
    }

    /**
     * Creates the IAB consent string out of the nescessary information
     * @param consentLanguage string The languagecode
     * @param vendorsAllowedArray array of vendor ids
     * @param purposesAllowedArray array of purpose ids
     */
    createConsentsStringAsBase64EncodedString(
        consentLanguage: string,
        vendorsAllowedArray,
        purposesAllowedArray
    ) {
        if (!this.globalVendorList) {
            return '';
        }

        const CONSENT_SCREEN: number = 1;
        const version = packageJson.version;
        const majorVersion: number = parseInt(version.charAt(0));

        const consentData = new ConsentString();
        consentData.setCmpId(Constants.USERCENTRICS_CMP_ID);
        consentData.setCmpVersion(majorVersion);
        consentData.setConsentScreen(CONSENT_SCREEN);
        consentData.setConsentLanguage(consentLanguage);
        consentData.setPurposesAllowed(purposesAllowedArray);
        consentData.setVendorsAllowed(vendorsAllowedArray);
        consentData.setGlobalVendorList(this.globalVendorList || {});

        return consentData.getConsentString();
    }

    private getEnabledPurposesFromCookie() {
        const consentString = this.readFirstPartyCookie();
        if (!consentString) {
            return [];
        }

        const decodedConsent = new ConsentString(consentString);
        return decodedConsent.allowedPurposeIds;
    }

    private readFirstPartyCookie() {
        return Cookies.get(Constants.CMP_IAB_COOKIE_NAME_FIRST_PARTY);
    }

    writeToEuConsentsCookie(data) {
        if (!data) {
            return;
        }

        if (this._iabIsActive) {
            const object = {
                cookieValue: data,
                //thirteen thirty day months
                cookieMaxAge: 13 * 30
            };
            this.writeFirstPartyCookie(object);
            this.writeThirdPartyCookie(object);
        }
    }

    private writeThirdPartyCookie(object) {
        object.cookieName = Constants.CMP_IAB_COOKIE_NAME_THIRD_PARTY;
        window.globalConsentCookie = object;
        const consensuIframeId: HTMLIFrameElement = document.getElementById('consensuIframe') as HTMLIFrameElement;
        if (consensuIframeId && typeof consensuIframeId !== 'undefined') {
            consensuIframeId.contentWindow.postMessage(object, '*');
        }
    }

    private writeFirstPartyCookie(object) {
        object.cookieName = Constants.CMP_IAB_COOKIE_NAME_FIRST_PARTY;
        if (object.cookieName && object.cookieValue && object.cookieMaxAge) {
            const cookieName = object.cookieName;
            const cookieValue = object.cookieValue;
            const expires = object.cookieMaxAge;
            Cookies.set(cookieName, cookieValue, { expires: expires });
        }
    }

    static deleteFirstPartyCookie() {
        Cookies.remove(Constants.CMP_IAB_COOKIE_NAME_FIRST_PARTY);
    }

    public getPurposeJson(language) {
        const allowedLanguages = ['en', 'bg', 'cs', 'da', 'de', 'el', 'es', 'et', 'fi', 'fr', 'ga', 'hr', 'hu', 'it', 'lt', 'lv', 'mt', 'nl', 'pl', 'pt', 'ro', 'sk', 'sl', 'sv', 'no', 'ru', 'zh'];
        if (allowedLanguages.indexOf(language) === -1) {
            console.error('purposes language does not exist: ' + language);
        }
        const object = {
            type: 'purposes',
            language: language
        };
        const consensuIframeId: HTMLIFrameElement = document.getElementById('consensuIframe') as HTMLIFrameElement;
        if (consensuIframeId && typeof consensuIframeId !== 'undefined') {
            consensuIframeId.contentWindow.postMessage(object, '*');
        }
    }

    getVendorDetail(iabId) {
        return this.vendors.find(vendor => {
            return vendor.id === iabId;
        });
    }

    private mappedPurposes(purposes, purposeIds) {
        return purposeIds.map(id => {
            return purposes.purposes.find(purpose => {
                return purpose.id === id;
            });
        });
    }

    async getPurposes(iabId, language) {
        const vendor = this.getVendorDetail(iabId);
        const purposeIds = vendor.purposeIds.concat(vendor.legIntPurposeIds);
        this.getPurposeJson(language);
        try {
            if (this.purposes && this.purposes.length) {
                return this.mappedPurposes(this.purposes, purposeIds);
            }

            await new Promise(resolve => setTimeout(resolve, 200));

            if (this.purposes && this.purposes.length) {
                return this.mappedPurposes(this.purposes, purposeIds);
            }

            return [];
        } catch (e) {
            console.log('error', e);
            return [];
        }
    }
}
